import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ExtracaoDadosData, ExtracaoDadosModalComponent, FiltroExtracao } from '../../../shared/components/extracao-dados-modal/extracao-dados-modal.component';
import { OrigemPedidoExportacaoEnum } from '../../../shared/enums/batch-extracao-dados/origem-pedido-exportacao.enum';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { LoadingBoxComponent } from '../../../shared/components/loading-box/loading-box.component';
import { LoadingBoxDefaultListComponent } from '../../../shared/components/loading-box-default-list/loading-box-default-list.component';
import { TipoExtracaoEnum } from '../../../shared/enums/batch-extracao-dados/tipo-extracao.enum';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { PermissaoEnum } from '@shared/enums/usuario/permissao.enum';
import { AuthService } from '@shared/services/apis/usuario/auth.service';
import { TabsScrollDirective } from '../../../shared/directives/tabs-scroll.directive';
import { AbntPipe } from '../../../shared/pipes/abnt.pipe';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { ControleEstoqueService } from '../../../shared/services/apis/produto/controle-estoque.service';
import { LookupService } from '../../../shared/services/apis/produto/produto-lookup.service';
import { LocalizacaoEstoque } from '../../../shared/services/models/produto/localizacao-estoque.model';
import { ControleEstoqueDetalheLotesRequest } from '../../../shared/services/requests/produto/controle-estoque-detalhe-lotes.request';
import { ControleEstoqueDetalheMovimentosRequest } from '../../../shared/services/requests/produto/controle-estoque-detalhe-movimentos.request';
import { ControleEstoqueDetalheReservasRequest } from '../../../shared/services/requests/produto/controle-estoque-detalhe-reservas.request';
import { ControleEstoqueDetalheSeriesRequest } from '../../../shared/services/requests/produto/controle-estoque-detalhe-series.request';
import { ControleEstoqueDetalheCabecalhoStruct } from '../../../shared/services/structs/produto/controle-estoque-detalhe-cabecalho.struct';
import { AbaLotesComponent } from './tabs/aba-lotes/aba-lotes.component';
import { AbaMovimentosComponent } from './tabs/aba-movimentos/aba-movimentos.component';
import { AbaReservasComponent } from './tabs/aba-reservas/aba-reservas.component';
import { AbaSeriesComponent } from './tabs/aba-series/aba-series.component';
import {
  ImpressaoControleEstoqueDetalheAba,
  ImpressaoControleEstoqueDetalheComponent,
} from './dialogs/impressao-controle-estoque-detalhe/impressao-controle-estoque-detalhe.component';
import {
  NovaMovimentacaoProdutoDialogComponent,
  NovaMovimentacaoProdutoDialogData,
} from './dialogs/nova-movimentacao-produto-dialog/nova-movimentacao-produto-dialog.component';
import {
  NovaReservaProdutoDialogComponent,
  NovaReservaProdutoDialogData,
} from './dialogs/nova-reserva-produto-dialog/nova-reserva-produto-dialog.component';

type AbaAtiva = 'movimentos' | 'reservas' | 'lotes' | 'series';

@Component({
  selector: 'app-controle-estoque-detalhe',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatTabsModule,
    TabsScrollDirective,
    MatTooltipModule,
    MatChipsModule,
    HeaderComponent,
    LoadingBoxComponent,
    LoadingBoxDefaultListComponent,
    AbaMovimentosComponent,
    AbaReservasComponent,
    AbaLotesComponent,
    AbaSeriesComponent,
    AbntPipe,
  ],
  templateUrl: './controle-estoque-detalhe.component.html',
  styleUrls: ['./controle-estoque-detalhe.component.css'],
})
export class ControleEstoqueDetalheComponent implements OnInit, OnDestroy {
  public moduloEnum = ModuloEnum.Suprimentos;
  public funcionalidadeEnum = FuncionalidadeEnum.Suprimentos_ControleEstoque;

  public idProduto: number = 0;
  public _isLoading: boolean = false;
  public cabecalho?: ControleEstoqueDetalheCabecalhoStruct;
  public podeVisualizarCusto: boolean = false;
  /** Produto pode ser reservado (não é kit, pai de variação, inativo nem sem controle de estoque). */
  public isPermiteReserva: boolean = false;

  public listaLocalizacaoEstoque: LocalizacaoEstoque[] = [];

  // Permissões de "Reserva de estoque". Sem Visualizar a aba não aparece; os índices das abas
  // seguintes (abaAtiva) já descontam a aba Reservas quando ela está oculta.
  public mostrarAbaReservas: boolean = false;
  public podeInserirReserva: boolean = false;
  public podeExcluirReserva: boolean = false;

  public abaAtivaIndex: number = 0;
  public filtroLocalAtual: number | null = null;
  public temReservasSelecionadas: boolean = false;
  public isMobileDrawerOpen: boolean = false;

  @ViewChild(AbaMovimentosComponent) abaMovimentosRef?: AbaMovimentosComponent;
  @ViewChild(AbaReservasComponent) abaReservasRef?: AbaReservasComponent;
  @ViewChild(AbaLotesComponent) abaLotesRef?: AbaLotesComponent;
  @ViewChild(AbaSeriesComponent) abaSeriesRef?: AbaSeriesComponent;

  constructor(
    private route: ActivatedRoute,
    private router: Router,

    private controleEstoqueService: ControleEstoqueService,
    private lookupService: LookupService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    const permissoes = this.authService.getUserInfos()?.listaPermissoes ?? [];
    this.mostrarAbaReservas = permissoes.includes(PermissaoEnum.Suprimentos_ControleEstoque_ReservaEstoque_Visualizar);
    this.podeInserirReserva = permissoes.includes(PermissaoEnum.Suprimentos_ControleEstoque_ReservaEstoque_Inserir);
    this.podeExcluirReserva = permissoes.includes(PermissaoEnum.Suprimentos_ControleEstoque_ReservaEstoque_Excluir);

    const idParam = this.route.snapshot.paramMap.get('idProduto');
    this.idProduto = idParam ? Number(idParam) : 0;

    if (!this.idProduto) {
      this.alertService.show('Erro', 'Produto inválido', AlertType.error);
      return;
    }

    this.carregarLookups();
    this.carregarCabecalho();
  }

  ngOnDestroy(): void {
    this.resetBodyOverflow();
  }

  public voltar(): void {
    this.fecharGavetaMobile();
    this.router.navigate(['/suprimentos/controle-estoque']);
  }

  public onAbaChange(index: number): void {
    this.abaAtivaIndex = index;
    this.fecharGavetaMobile();
  }

  public toggleGavetaMobile(): void {
    this.isMobileDrawerOpen = !this.isMobileDrawerOpen;
    this.setBodyOverflow(this.isMobileDrawerOpen);
  }

  public fecharGavetaMobile(): void {
    if (!this.isMobileDrawerOpen) return;
    this.isMobileDrawerOpen = false;
    this.resetBodyOverflow();
  }

  public get abaAtiva(): AbaAtiva {
    let idx = 0;
    if (this.abaAtivaIndex === idx) return 'movimentos';
    idx++;

    if (this.mostrarAbaReservas) {
      if (this.abaAtivaIndex === idx) return 'reservas';
      idx++;
    }

    if (this.cabecalho?.isControlaLotes) {
      if (this.abaAtivaIndex === idx) return 'lotes';
      idx++;
    }

    if (this.cabecalho?.isControlaSeries) {
      if (this.abaAtivaIndex === idx) return 'series';
    }

    return 'movimentos';
  }

  public get abaMovimentosAtiva(): boolean {
    return this.abaAtiva === 'movimentos';
  }

  public get abaReservasAtiva(): boolean {
    return this.abaAtiva === 'reservas';
  }

  public get abaLotesAtiva(): boolean {
    return this.abaAtiva === 'lotes';
  }

  public get abaSeriesAtiva(): boolean {
    return this.abaAtiva === 'series';
  }

  public onFiltroLocalChange(idLocal: number | null): void {
    this.filtroLocalAtual = idLocal;
    this.carregarCabecalho();
  }

  public onMovimentoAlterado(): void {
    this.abaLotesRef?.clickPesquisar();
    this.abaSeriesRef?.clickPesquisar();
  }

  public onSelecaoReservasChange(temSelecao: boolean): void {
    this.temReservasSelecionadas = temSelecao;
  }

  public executarStub(rotina: string): void {
    this.alertService.show('Atenção', `${rotina} será implementado em uma próxima etapa.`, AlertType.warning);
  }

  public imprimirRelatorio(): void {
    const request = this.getRequestImpressaoAbaAtiva();
    if (!request) {
      this.alertService.show('Atenção', 'Não foi possível montar os filtros de impressão da aba ativa.', AlertType.warning);
      return;
    }

    this.dialog.open(ImpressaoControleEstoqueDetalheComponent, {
      data: {
        abaAtiva: this.abaAtiva as ImpressaoControleEstoqueDetalheAba,
        idProduto: this.idProduto,
        tituloAba: this.tituloAbaAtiva(),
        descricaoProduto: this.getDescricaoProduto(),
        request,
        colunasMovimentos: this.abaMovimentosRef?.getColunasVisiveisSaida(),
      },
      maxWidth: '90vw',
      disableClose: false,
    });
  }

  public exportar(): void {
    if (!this.cabecalho) return;

    const filtros: FiltroExtracao[] = [
      {
        label: 'Produto',
        formControlName: 'idProduto',
        valor: this.idProduto,
        valorExibicao: this.getDescricaoProduto(),
      },
      ...this.getFiltrosExtracaoAbaAtiva(),
    ];

    const dialogData: ExtracaoDadosData = {
      titulo: `Exportação - ${this.tituloAbaAtiva()}`,
      tipoExtracao: this.getTipoExtracaoAbaAtiva(),
      origemPedidoExportacao: OrigemPedidoExportacaoEnum.SuprimentosControleEstoque,
      filtros,
      possuiAutomacao: false,
    };

    this.dialog.open(ExtracaoDadosModalComponent, {
      data: dialogData,
      panelClass: 'extracao-dados-panel',
      disableClose: true,
    });
  }

  public novoMovimento(): void {
    if (!this.cabecalho) return;

    const data: NovaMovimentacaoProdutoDialogData = {
      idProduto: this.idProduto,
      descricaoProduto: this.cabecalho.descricao,
      skuProduto: this.cabecalho.codigoSku,
      isControlaLotes: this.cabecalho.isControlaLotes,
      isControlaSeries: this.cabecalho.isControlaSeries,
      isFracionada: this.cabecalho.isUnidadeMedidaFracionada,
      idLocalizacaoEstoque: this.localInicialLancamento(this.abaMovimentosRef?.getRequestAtual().idLocalizacaoEstoque),
    };

    this.dialog.open(NovaMovimentacaoProdutoDialogComponent, {
      data,
      width: '900px',
      maxWidth: '96vw',
      maxHeight: '95vh',
      disableClose: true,
    }).afterClosed().subscribe(result => {
      if (!result?.confirmed) return;

      this.carregarCabecalho();
      this.abaMovimentosRef?.clickPesquisar();
      this.abaLotesRef?.clickPesquisar();
      this.abaSeriesRef?.clickPesquisar();
    });
  }

  public get tooltipNovaReserva(): string {
    if (!this.podeInserirReserva) return 'Sem permissão para reservar';
    if (!this.isPermiteReserva) return 'Este produto não permite reserva';
    return '';
  }

  public novaReserva(): void {
    if (!this.cabecalho || !this.podeInserirReserva || !this.isPermiteReserva) return;

    const data: NovaReservaProdutoDialogData = {
      idProduto: this.idProduto,
      descricaoProduto: this.cabecalho.descricao,
      skuProduto: this.cabecalho.codigoSku,
      unidade: this.cabecalho.unidade,
      isControlaLotes: this.cabecalho.isControlaLotes,
      isControlaSeries: this.cabecalho.isControlaSeries,
      isFracionada: this.cabecalho.isUnidadeMedidaFracionada,
      idLocalizacaoEstoque: this.localInicialLancamento(this.abaReservasRef?.getRequestAtual().idLocalizacaoEstoque),
    };

    this.dialog.open(NovaReservaProdutoDialogComponent, {
      data,
      width: '900px',
      maxWidth: '96vw',
      maxHeight: '95vh',
      disableClose: true,
    }).afterClosed().subscribe(result => {
      if (!result?.confirmed) return;

      this.carregarCabecalho();
      this.abaReservasRef?.clickPesquisar();
    });
  }

  // O lançamento abre no local filtrado na aba; com o filtro em "todos", no local padrão do sistema.
  private localInicialLancamento(idLocalFiltro?: number | null): number | null {
    if (idLocalFiltro) return idLocalFiltro;
    return this.listaLocalizacaoEstoque.find(l => l.isPadrao)?.idLocalizacaoEstoque ?? null;
  }

  // Refresca os totalizadores quando a aba libera reservas.
  public onReservaLiberada(): void {
    this.carregarCabecalho();
  }

  public excluirReservasSelecionadas(): void {
    this.abaReservasRef?.excluirSelecionadas();
  }

  private getDescricaoProduto(): string {
    if (!this.cabecalho) return `${this.idProduto}`;
    return `${this.cabecalho.descricao}${this.cabecalho.codigoSku ? ' (' + this.cabecalho.codigoSku + ')' : ''}`;
  }

  private getTipoExtracaoAbaAtiva(): TipoExtracaoEnum {
    if (this.abaAtiva === 'movimentos') return TipoExtracaoEnum.ControleEstoqueMovimentacoesCSV;
    if (this.abaAtiva === 'reservas') return TipoExtracaoEnum.ControleEstoqueReservasCSV;
    if (this.abaAtiva === 'lotes') return TipoExtracaoEnum.ControleEstoqueLotesCSV;
    if (this.abaAtiva === 'series') return TipoExtracaoEnum.ControleEstoqueSeriesCSV;
    return TipoExtracaoEnum.ControleEstoqueCSV;
  }

  private getFiltrosExtracaoAbaAtiva(): FiltroExtracao[] {
    if (this.abaAtiva === 'movimentos') return this.abaMovimentosRef?.getFiltrosExtracao() || [];
    if (this.abaAtiva === 'reservas') return this.abaReservasRef?.getFiltrosExtracao() || [];
    if (this.abaAtiva === 'lotes') return this.abaLotesRef?.getFiltrosExtracao() || [];
    if (this.abaAtiva === 'series') return this.abaSeriesRef?.getFiltrosExtracao() || [];
    return [];
  }

  private getRequestImpressaoAbaAtiva():
    | ControleEstoqueDetalheMovimentosRequest
    | ControleEstoqueDetalheReservasRequest
    | ControleEstoqueDetalheLotesRequest
    | ControleEstoqueDetalheSeriesRequest
    | null {
    if (this.abaAtiva === 'movimentos') return this.abaMovimentosRef?.getRequestAtual() || null;
    if (this.abaAtiva === 'reservas') return this.abaReservasRef?.getRequestAtual() || null;
    if (this.abaAtiva === 'lotes') return this.abaLotesRef?.getRequestAtual() || null;
    if (this.abaAtiva === 'series') return this.abaSeriesRef?.getRequestAtual() || null;
    return null;
  }

  private tituloAbaAtiva(): string {
    if (this.abaAtiva === 'movimentos') return 'Movimentações';
    if (this.abaAtiva === 'reservas') return 'Reservas';
    if (this.abaAtiva === 'lotes') return 'Lotes e Validades';
    if (this.abaAtiva === 'series') return 'Séries';
    return 'Controle de Estoque';
  }

  private carregarLookups(): void {
    this.lookupService.getLookup().subscribe({
      next: res => {
        if (!res.isError) {
          this.listaLocalizacaoEstoque = res.listaLocalizacaoEstoque || [];
        }
      },
    });
  }

  private carregarCabecalho(): void {
    this._isLoading = true;
    this.controleEstoqueService.getCabecalho(this.idProduto, this.filtroLocalAtual).subscribe({
      next: response => {
        this._isLoading = false;
        if (response.isError) {
          this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
          return;
        }
        this.cabecalho = response.cabecalho;
        this.podeVisualizarCusto = response.podeVisualizarCusto;
        this.isPermiteReserva = response.isPermiteReserva;
      },
      error: err => {
        this._isLoading = false;
        this.alertService.show('Erro inesperado', err?.message || err, AlertType.error);
      },
    });
  }

  private resetBodyOverflow(): void {
    this.setBodyOverflow(false);
  }

  private setBodyOverflow(isHidden: boolean): void {
    if (typeof document === 'undefined') return;
    document.body.style.overflow = isHidden ? 'hidden' : '';
  }
}
